/**
 * ICS (iCalendar) file generator for calendar subscriptions
 * Follows RFC 5545 specification
 *
 * Shared between client-side exports and server-side API
 */

export interface ICSEvent {
  id: string | number;
  title: string;
  notes?: string;
  dates: string[];
  all_day?: boolean;
  start_time?: string | null;
  end_time?: string | null;
}

export interface ICSOptions {
  /** Include refresh interval hints for subscription clients */
  includeRefreshInterval?: boolean;
  /** Domain for event UIDs */
  domain?: string;
}

/**
 * Escape special characters for ICS format
 */
function escapeICSText(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Format date for ICS (YYYYMMDD for all-day, YYYYMMDDTHHmmss for timed)
 */
function formatICSDate(dateStr: string, time?: string | null, isEnd?: boolean): string {
  const [year, month, day] = dateStr.split('-');

  if (time) {
    const [hours, minutes] = time.split(':');
    return `${year}${month}${day}T${hours}${minutes}00`;
  }

  // All-day event - for end date, add 1 day (ICS uses exclusive end)
  if (isEnd) {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + 1);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  return `${year}${month}${day}`;
}

/**
 * Fold long lines according to ICS spec (max 75 chars)
 */
function foldLine(line: string): string {
  if (line.length <= 75) return line;

  const result: string[] = [];
  let remaining = line;

  while (remaining.length > 75) {
    result.push(remaining.substring(0, 75));
    remaining = ' ' + remaining.substring(75);
  }
  result.push(remaining);

  return result.join('\r\n');
}

/**
 * Generate ICS content from events
 */
export function generateICS(
  events: ICSEvent[],
  calendarName: string,
  options: ICSOptions = {}
): string {
  const { includeRefreshInterval = false, domain = 'mozayc.io' } = options;

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Mozayc//EN',
    `X-WR-CALNAME:${escapeICSText(calendarName)}`,
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  // Add refresh interval hints for subscription calendars
  if (includeRefreshInterval) {
    lines.push('REFRESH-INTERVAL;VALUE=DURATION:PT15M');
    lines.push('X-PUBLISHED-TTL:PT15M');
  }

  const now = new Date();
  const timestamp = now.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

  for (const event of events) {
    const startDate = event.dates[0];
    const endDate = event.dates[event.dates.length - 1];

    if (!startDate) continue;

    lines.push('BEGIN:VEVENT');
    lines.push(foldLine(`UID:${event.id}@${domain}`));
    lines.push(`DTSTAMP:${timestamp}`);

    if (event.all_day !== false && !event.start_time) {
      lines.push(`DTSTART;VALUE=DATE:${formatICSDate(startDate)}`);
      lines.push(`DTEND;VALUE=DATE:${formatICSDate(endDate, null, true)}`);
    } else {
      const startTime = event.start_time || '09:00';
      const endTime = event.end_time || event.start_time || '10:00';
      lines.push(`DTSTART:${formatICSDate(startDate, startTime)}`);
      lines.push(`DTEND:${formatICSDate(endDate, endTime)}`);
    }

    lines.push(foldLine(`SUMMARY:${escapeICSText(event.title)}`));

    if (event.notes) {
      lines.push(foldLine(`DESCRIPTION:${escapeICSText(event.notes)}`));
    }

    lines.push('END:VEVENT');
  }

  lines.push('END:VCALENDAR');

  return lines.join('\r\n');
}
